/**
 * Payer-Specialty Adjustment Helpers (v3)
 *
 * Pure functions that compute weighted indices from an org's payer mix,
 * specialty mix, and the reference tables in benchmarks.ts. No side
 * effects — every function takes explicit inputs and returns a number.
 */

import { sumTotalEncounterVolumes } from "../profile/encounter-volumes";
import type { OrgType } from "../profile/types";
import {
  CMI_PEER_BENCHMARK,
  CONTRIBUTION_MARGIN_BY_SETTING,
  COVERAGE_DISCOVERY_PAYER_MIX,
  INPATIENT_CODING_COMPLEXITY,
  PAYER_DENIAL_RATE,
  PAYER_NOSHOW_MULTIPLIER,
  PAYER_REIMBURSEMENT_INDEX,
  SELF_PAY_SETTING_MIX,
  SPECIALTY_PHYSICIAN_COST,
  SPECIALTY_REVENUE_MULTIPLIER,
  SURGICAL_PAYER_ADJUSTMENT,
} from "./reference-data";

// ── Utility ──────────────────────────────────────────────────

/** Re-normalize a Record<string, number> so values sum to 1.0. */
function normalize(mix: Record<string, number>): Record<string, number> {
  const sum = Object.values(mix).reduce((a, b) => a + b, 0);
  if (sum <= 0) return { ...mix };
  const result: Record<string, number> = {};
  for (const [k, v] of Object.entries(mix)) {
    result[k] = v / sum;
  }
  return result;
}

// ── Payer Revenue Index ──────────────────────────────────────

/**
 * Weighted average reimbursement index for the org's payer mix.
 * 1.0 = system-wide average; >1 means above-average reimbursement.
 */
function computePayerRevenueIndex(payerMix: Record<string, number>): number {
  let index = 0;
  for (const [payer, share] of Object.entries(payerMix)) {
    index += share * (PAYER_REIMBURSEMENT_INDEX[payer] ?? 0.8);
  }
  return index || 0.8;
}

// ── Coverage Discovery ───────────────────────────────────────

/**
 * Net discovery index: the reimbursement value of discovered coverage
 * minus what self-pay would have yielded. Typical AMC: ~0.54.
 */
export function computeNetDiscoveryIndex(): number {
  let discoveryReimb = 0;
  for (const [payer, share] of Object.entries(COVERAGE_DISCOVERY_PAYER_MIX)) {
    discoveryReimb += share * (PAYER_REIMBURSEMENT_INDEX[payer] ?? 0.5);
  }
  const selfPayReimb = PAYER_REIMBURSEMENT_INDEX.self_pay ?? 0.18;
  return discoveryReimb - selfPayReimb;
}

/**
 * Setting adjustment: ratio of self-pay encounter setting mix
 * vs. org-wide average revenue per setting. ED-heavy self-pay
 * encounters have different value than ambulatory ones.
 */
export function computeSettingAdjustment(
  encounterVolumes: Record<string, number>,
): number {
  const totalEnc = sumTotalEncounterVolumes(encounterVolumes) ?? 0;
  if (totalEnc === 0) return 1.0;

  // Weight self-pay setting mix by each setting's share of org encounters
  let adjustment = 0;
  for (const [setting, selfPayShare] of Object.entries(SELF_PAY_SETTING_MIX)) {
    const orgShare = (encounterVolumes[setting] ?? 0) / totalEnc;
    // If org has more of this setting than self-pay expects, it's a wash;
    // if org is heavily weighted toward that setting, self-pay follows.
    adjustment +=
      selfPayShare * Math.min(1.5, Math.max(0.5, orgShare > 0 ? 1.0 : 0.5));
  }
  return adjustment || 1.0;
}

// ── No-Show Pool ─────────────────────────────────────────────

/**
 * Compute the payer distribution of the no-show pool. Patients who
 * no-show skew toward Medicaid and self-pay.
 */
function computeNoshowPoolPayerMix(
  orgPayerMix: Record<string, number>,
): Record<string, number> {
  const raw: Record<string, number> = {};
  for (const [payer, share] of Object.entries(orgPayerMix)) {
    raw[payer] = share * (PAYER_NOSHOW_MULTIPLIER[payer] ?? 1.0);
  }
  return normalize(raw);
}

/**
 * Revenue index for the no-show pool. Since no-show patients
 * skew to lower-reimbursement payers, recovered no-shows are
 * worth less than the average appointment.
 */
export function computeNoshowRevenueIndex(
  orgPayerMix: Record<string, number>,
): number {
  const noshowMix = computeNoshowPoolPayerMix(orgPayerMix);
  return computePayerRevenueIndex(noshowMix);
}

/**
 * Specialty-weighted multiplier for no-show appointment value.
 * High-revenue specialties that have significant no-shows
 * raise the per-appointment recovery value.
 */
export function computeSpecialtyWeightedNoshowValue(
  specialtyMix: Record<string, number>,
): number {
  let mult = 0;
  for (const [spec, weight] of Object.entries(specialtyMix)) {
    mult += weight * (SPECIALTY_REVENUE_MULTIPLIER[spec] ?? 1.0);
  }
  return mult || 1.0;
}

// ── Denial Pool ──────────────────────────────────────────────

/**
 * Org-wide payer-weighted denial rate.
 */
export function computeOrgDenialRate(payerMix: Record<string, number>): number {
  let rate = 0;
  for (const [payer, share] of Object.entries(payerMix)) {
    rate += share * (PAYER_DENIAL_RATE[payer] ?? 0.08);
  }
  return rate;
}

/**
 * Payer distribution of denied claims. Denials skew toward
 * commercial and MA (higher denial rates).
 */
function computeDenialPoolPayerMix(
  payerMix: Record<string, number>,
): Record<string, number> {
  const raw: Record<string, number> = {};
  for (const [payer, share] of Object.entries(payerMix)) {
    const denialRate = PAYER_DENIAL_RATE[payer] ?? 0.08;
    raw[payer] = share * denialRate;
  }
  return normalize(raw);
}

/**
 * Revenue index for denied claims. Denials skew commercial,
 * so recovered revenue per denial is typically above-average.
 */
export function computeDenialRevenueIndex(
  payerMix: Record<string, number>,
): number {
  const denialMix = computeDenialPoolPayerMix(payerMix);
  return computePayerRevenueIndex(denialMix);
}

/**
 * Marginal prevention factor: orgs with high existing denial rates
 * have more low-hanging fruit. Factor ranges 0.45–0.85.
 */
export function computeMarginalPreventionFactor(orgDenialRate: number): number {
  // Higher denial rate → more marginal prevention opportunity
  // but diminishing returns. Linear interpolation:
  // 5% denial rate → 0.45, 15% → 0.85
  const clamped = Math.max(0.05, Math.min(0.15, orgDenialRate));
  return 0.45 + ((clamped - 0.05) / 0.1) * 0.4;
}

// ── Ambient Documentation ────────────────────────────────────

/**
 * Specialty-weighted physician cost for ambient documentation.
 * Documentation-heavy specialties tend to have lower physician costs,
 * so this gives a more accurate per-physician value.
 */
export function computeAmbientWeightedPhysicianCost(
  specialtyMix: Record<string, number>,
): number {
  let cost = 0;
  for (const [spec, weight] of Object.entries(specialtyMix)) {
    cost += weight * (SPECIALTY_PHYSICIAN_COST[spec] ?? 400_000);
  }
  return cost || 400_000;
}

// ── Autonomous Coding ────────────────────────────────────────

/**
 * Specialty-weighted inpatient coding time. Complex specialties
 * (surgery, oncology) take longer to code.
 */
export function computeWeightedInpatientCodingTime(
  specialtyMix: Record<string, number>,
  baseIpTime: number,
): number {
  let complexity = 0;
  for (const [spec, weight] of Object.entries(specialtyMix)) {
    complexity += weight * (INPATIENT_CODING_COMPLEXITY[spec] ?? 1.0);
  }
  return baseIpTime * (complexity || 1.0);
}

// ── PA Automation ────────────────────────────────────────────

/**
 * Weighted contribution margin across PA-heavy service settings.
 * PA-required services skew toward imaging and specialty procedures.
 */
export function computeWeightedContributionMargin(): number {
  // PA-service setting distribution (imaging-heavy)
  const paSettingMix: Record<string, number> = {
    imaging: 0.3,
    outpatient: 0.25,
    surgical: 0.2,
    inpatient: 0.15,
    ambulatory: 0.1,
  };
  let margin = 0;
  for (const [setting, share] of Object.entries(paSettingMix)) {
    margin += share * (CONTRIBUTION_MARGIN_BY_SETTING[setting] ?? 0.35);
  }
  return margin || 0.35;
}

// ── OR Optimization ──────────────────────────────────────────

/**
 * Adjust org payer mix for surgical skew. Surgical cases
 * over-index on commercial and under-index on self-pay.
 */
function computeSurgicalPayerMix(
  orgPayerMix: Record<string, number>,
): Record<string, number> {
  const raw: Record<string, number> = {};
  for (const [payer, share] of Object.entries(orgPayerMix)) {
    raw[payer] = share * (SURGICAL_PAYER_ADJUSTMENT[payer] ?? 1.0);
  }
  return normalize(raw);
}

/**
 * Revenue index for incremental OR cases, adjusted for
 * surgical payer mix skew.
 */
export function computeSurgicalRevenueIndex(
  orgPayerMix: Record<string, number>,
): number {
  const surgMix = computeSurgicalPayerMix(orgPayerMix);
  return computePayerRevenueIndex(surgMix);
}

// ── CDI ──────────────────────────────────────────────────────

/**
 * CMI durability factor: how sustainable is a CMI improvement?
 * Orgs already near or above peer benchmark have less room.
 * Returns 0.55–0.85.
 */
export function computeCMIDurability(orgCMI: number, orgType: OrgType): number {
  const peer = CMI_PEER_BENCHMARK[orgType] ?? 1.45;
  const ratio = orgCMI / peer;
  // Below peer → more room → higher durability
  // At or above peer → harder to sustain
  if (ratio < 0.9) return 0.85;
  if (ratio < 0.95) return 0.75;
  if (ratio < 1.0) return 0.65;
  return 0.55; // At or above peer benchmark
}
